import { FC, useState, MouseEvent, useContext } from 'react'
import { Box, Menu, Button, MenuItem, SvgIconTypeMap } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { useToast } from '../../../../contexts/toast.context'
import AllSFAFsModal from '../../Modals/AllSFAFsModal'
import { SelectWithSearch } from '../../../shared/SearchSelect'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'
import { RequestContext } from '../../../../contexts/request.context'
import { ISpecManRequestFrequency } from '../../../../../shared/types/api/spectrum-manager.type'
import { FrequencyPlansTableConfigs } from '../../../../utils/constants'

import styles from './styles.module.scss'

interface IProps {
  selectedRequest?: ISpecManRequestFrequency | null
  tabs?: Array<{
    label: string
    icon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
      muiName: string
    }
  }>
}

const Toolbar: FC<IProps> = ({ tabs, selectedRequest }) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [isAllSFAFOpen, setIsAllSFAFOpen] = useState(false)
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)

  const { showToast } = useToast()
  const { exercises, locationTemplates } = useContext(RequestContext)
  const { filterOptions, frequencyPlans, setFilterOptions } = useContext(SpectrumManagerContext)

  const locationOptions = locationTemplates.map((loc) => ({
    value: loc.id,
    label: loc.transmitter_location || loc.template_name,
  }))

  const handleFrequencyPlanExport = () => {
    const data = frequencyPlans

    if (!data || !data.length) {
      showToast('warning', 'No data to export')
      return
    }

    const headers = FrequencyPlansTableConfigs.items.map((item) => item.column)

    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        FrequencyPlansTableConfigs.items
          .map((item) => JSON.stringify((row as any)[item.key as string] || ''))
          .join(','),
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `frequency_plan.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setIsDropDownOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsDropDownOpen(false)
  }

  return (
    <Box className={styles.toolbar}>
      <Box className={styles.leftContainer}></Box>
      <Box className={styles.rightContainer}>
        <Box className={styles.exportContainer}>
          <Button
            onClick={handleClick}
            className={styles.exportBtn}
            variant="outlined"
            id="basic-button"
            aria-controls={isDropDownOpen ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isDropDownOpen ? 'true' : undefined}
          >
            Export
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={isDropDownOpen}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem onClick={() => setIsAllSFAFOpen(true)}>SFAFs</MenuItem>
            <MenuItem onClick={() => handleFrequencyPlanExport()}>Frequency Plan</MenuItem>
          </Menu>
        </Box>
        <SelectWithSearch
          label="Exercise"
          height={40}
          width={200}
          value={exercises
            .map((exercise) => ({
              value: exercise.id,
              label: exercise.name,
            }))
            .find((elem) => elem.value === filterOptions.exerciseId)}
          options={exercises.map((exercise) => ({
            value: exercise.id,
            label: exercise.name,
          }))}
          handleChange={(value) =>
            setFilterOptions((prev) => ({
              ...prev,
              exerciseId: value ? Number(value) : undefined,
            }))
          }
        />
        <SelectWithSearch
          width={200}
          height={40}
          label="Status"
          value={['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'TEST', 'INCOMPLETE']
            .map((value) => ({ value, label: value }))
            .find((elem) => elem.value === filterOptions.status)}
          options={['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'TEST', 'INCOMPLETE'].map((value) => ({
            value,
            label: value,
          }))}
          handleChange={(value) =>
            setFilterOptions((prev) => ({
              ...prev,
              status: value ? String(value) : undefined,
            }))
          }
        />
        <SelectWithSearch
          width={200}
          height={40}
          options={locationOptions}
          value={locationOptions.find((elem) => elem.value === filterOptions.locationId)}
          handleChange={(value) =>
            setFilterOptions((prev) => ({
              ...prev,
              locationId: value ? Number(value) : undefined,
            }))
          }
          label="Location"
        />
      </Box>
      <AllSFAFsModal
        isOpen={isAllSFAFOpen}
        onClose={() => setIsAllSFAFOpen(false)}
        frequency_group_id={selectedRequest?.frequency_group_id || 0}
      />
    </Box>
  )
}

export default Toolbar
